// Parent-owned tracking for a backgrounded (immediate) publish.
//
// WHY this lives in App and not the Composer: the compose panel is a SlideOver
// that UNMOUNTS its children when closed, and the Composer's unmount cleanup
// tears down its own dispatch poll + History-refresh timers. So a user who fires
// a publish and then closes the panel mid-flight would lose every calendar /
// History refresh — the post would land on the platforms but never appear on
// screen until a manual reload. Hoisting the durable polling here keeps it alive
// regardless of the panel's mount state.
//
// Keyed on the in-flight idempotency key: a new key supersedes the previous
// poll, and `null` tears everything down. The Composer keeps its own inline
// result panel + poll for the live per-platform reveal while it is open; this
// hook is only responsible for the refreshes that MUST outlive it.

import { useEffect, useRef } from "react";
import { api } from "../api";

/** Poll window (ms) mirroring the Composer's leg-by-leg History refreshes: a
 * multi-platform video can take ~60-90s as each platform transcodes. */
const REFRESH_SCHEDULE_MS = [3000, 8000, 15000, 25000, 40000, 60000, 90000];
const POLL_INTERVAL_MS = 3000;
const POLL_CAP_MS = 120000;

/**
 * Track one in-flight publish identified by `key`. Refreshes the dashboard as
 * each leg lands and calls `onDone(completed)` once the recorded row arrives
 * (`completed = true`) or the poll caps out first (`completed = false`), so the
 * caller can clear the key and — when the compose page isn't showing its own
 * result overlay — announce the outcome accurately.
 */
export function useDispatchTracking(
  key: string | null,
  onRefresh: () => void,
  onDone: (completed: boolean) => void,
): void {
  // Hold the latest callbacks in refs so changing them never restarts the poll —
  // only the key does.
  const refreshRef = useRef(onRefresh);
  const doneRef = useRef(onDone);
  refreshRef.current = onRefresh;
  doneRef.current = onDone;

  useEffect(() => {
    if (!key) return;
    let stopped = false;

    const timers = REFRESH_SCHEDULE_MS.map((ms) =>
      window.setTimeout(() => {
        if (!stopped) refreshRef.current();
      }, ms),
    );

    const startedAt = Date.now();
    const poll = window.setInterval(async () => {
      try {
        const { record } = await api.dispatchStatus(key);
        if (stopped) return;
        if (record) {
          refreshRef.current();
          doneRef.current(true);
          return; // key flips to null → cleanup below clears the interval
        }
      } catch {
        // A transient status error is not fatal — keep polling until the cap.
      }
      if (!stopped && Date.now() - startedAt >= POLL_CAP_MS) doneRef.current(false);
    }, POLL_INTERVAL_MS);

    return () => {
      stopped = true;
      timers.forEach((t) => window.clearTimeout(t));
      window.clearInterval(poll);
    };
  }, [key]);
}
